import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, ShieldCheck, Download, CheckCircle2, XCircle, ChevronDown, ChevronUp, BrainCircuit, Zap, Coffee, Baby, FileText, Timer, AlertTriangle, Sparkles } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const Navbar = () => <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-brand-teal/10 shadow-sm" data-id="element-1">
    <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between" data-id="element-2">
      <div className="flex items-center gap-2 text-brand-teal font-serif text-xl" data-id="element-3">
        <Clock className="w-5 h-5 text-brand-orange" data-id="element-4" />
        <span data-id="element-5">Kit Silencio</span>
      </div>
      <a href="#cta-final" className="bg-brand-orange hover:bg-brand-orange-dark text-white px-5 py-2 rounded-full font-bold text-sm transition-colors shadow-md shadow-brand-orange/20" data-id="element-6">
        Quiero el Kit
      </a>
    </div>
  </header>;
const Hero = () => <section className="pt-32 pb-20 px-4 bg-brand-cream overflow-hidden relative" data-id="element-7">
    {/* Decorative blobs */}
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-8">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl" data-id="element-9"></div>
      <div className="absolute top-1/2 -left-24 w-72 h-72 bg-brand-orange/5 rounded-full blur-3xl" data-id="element-10"></div>
    </div>

    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-11">
      <FadeIn data-id="element-12">
        <div className="space-y-6" data-id="element-13">
          <div className="inline-flex items-center gap-2 bg-brand-teal-light text-brand-teal px-4 py-1.5 rounded-full text-sm font-bold tracking-wide uppercase" data-id="element-14">
            <Sparkles className="w-4 h-4" data-id="element-15" />
            Para padres que hacen Home Office
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-teal leading-tight" data-id="element-16">
            El Kit de Supervivencia: <br data-id="element-17" />
            <span className="text-brand-orange" data-id="element-18">
              30 Minutos de Silencio
            </span>{' '}
            <br data-id="element-19" />
            <span className="text-3xl md:text-4xl text-brand-slate" data-id="element-20">
              (Sin Pantallas)
            </span>
          </h1>
          <p className="text-lg md:text-xl text-brand-slate-light leading-relaxed" data-id="element-21">
            La guía estratégica para padres que trabajan desde casa y necesitan
            concentrarse sin recurrir a la niñera digital.
          </p>

          <div className="pt-4 space-y-4" data-id="element-22">
            <a href="#cta-final" className="block w-full sm:w-auto text-center bg-brand-orange hover:bg-brand-orange-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-transform hover:scale-105 shadow-xl shadow-brand-orange/20" data-id="element-23">
              Quiero mis 30 minutos de silencio
            </a>
            <div className="flex items-center justify-center sm:justify-start gap-4 text-sm text-brand-slate-light font-medium" data-id="element-24">
              <span className="flex items-center gap-1" data-id="element-25">
                <Download className="w-4 h-4 text-brand-teal" data-id="element-26" /> Acceso
                instantáneo en PDF
              </span>
              <span className="flex items-center gap-1" data-id="element-27">
                <ShieldCheck className="w-4 h-4 text-brand-teal" data-id="element-28" /> Pago único
              </span>
            </div>
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.2} data-id="element-29">
        <div className="relative" data-id="element-30">
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-brand-orange/20 rounded-3xl transform rotate-3 scale-105 blur-lg" data-id="element-31"></div>
          <img src="https://cdn.magicpatterns.com/uploads/hLsojZjmUpFwHZ7BAV5MKX/30_MINUTOS_DE_SILENCIO_PORTADA.png" alt="Kit Silencio Sin Pantallas Mockup" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-32" />
        </div>
      </FadeIn>
    </div>
  </section>;
const OpeningHook = () => <section className="py-20 px-4 bg-brand-teal text-white relative" data-id="element-33">
    <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-34">
      <FadeIn data-id="element-35">
        <AlertTriangle className="w-12 h-12 text-brand-orange mx-auto mb-6" data-id="element-36" />
        <p className="text-2xl md:text-3xl font-serif leading-relaxed" data-id="element-37">
          "Faltan 5 minutos para tu reunión más importante del día. Tu hijo está
          empezando a aburrirse, el ruido sube de tono y tu pánico crece."
        </p>
      </FadeIn>
      <FadeIn delay={0.2} data-id="element-38">
        <p className="text-lg md:text-xl text-brand-cream/90 leading-relaxed" data-id="element-39">
          Sabes que si le das la tablet ahora, ganarás la paz momentánea, pero
          pagarás el precio más tarde con una rabieta monumental cuando se la
          quites.
        </p>
      </FadeIn>
      <FadeIn delay={0.4} data-id="element-40">
        <div className="bg-white/10 p-6 rounded-2xl border border-white/20 mt-8" data-id="element-41">
          <p className="text-xl font-bold text-brand-orange" data-id="element-42">
            No tiene por qué ser así.
          </p>
          <p className="mt-2 text-brand-cream" data-id="element-43">
            Existe una forma de lograr que tu hijo juegue de manera
            independiente y profunda, dándote el espacio profesional que
            necesitas sin una gota de culpa.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const ProblemSection = () => <section className="py-20 px-4 bg-white" data-id="element-44">
    <div className="max-w-4xl mx-auto" data-id="element-45">
      <FadeIn data-id="element-46">
        <div className="text-center mb-16" data-id="element-47">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-teal mb-6" data-id="element-48">
            El problema no es tu hijo, es el sistema de entretenimiento actual.
          </h2>
          <p className="text-lg text-brand-slate-light" data-id="element-49">
            Las pantallas sobreestimulan el cerebro infantil, reduciendo su
            capacidad de atención a segundos. Cuando intentas trabajar, ellos
            buscan ese estímulo en ti, convirtiéndose en interrupciones
            constantes.
          </p>
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-8" data-id="element-50">
        <FadeIn delay={0.1} data-id="element-51">
          <div className="bg-brand-cream p-8 rounded-3xl h-full border border-brand-teal/10" data-id="element-52">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-6" data-id="element-53">
              <XCircle className="w-6 h-6 text-red-500" data-id="element-54" />
            </div>
            <h3 className="text-xl font-bold text-brand-slate mb-4" data-id="element-55">
              La trampa de "Pinterest"
            </h3>
            <p className="text-brand-slate-light" data-id="element-56">
              La mayoría de las actividades fallan porque requieren que tú pases
              una hora preparando materiales caros y otra hora limpiando el
              desastre después. Si la preparación es más larga que el juego, no
              es una solución para un padre que trabaja; es otro problema.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-57">
          <div className="space-y-4" data-id="element-58">
            <h3 className="text-xl font-bold text-brand-slate mb-6" data-id="element-59">
              Por qué lo que has intentado no funciona:
            </h3>

            <div className="flex gap-4 items-start" data-id="element-60">
              <div className="mt-1" data-id="element-61">
                <XCircle className="w-5 h-5 text-brand-orange" data-id="element-62" />
              </div>
              <p className="text-brand-slate-light" data-id="element-63">
                <strong data-id="element-64">Los juguetes ruidosos</strong> pierden la novedad en 5
                minutos porque no ofrecen un reto real.
              </p>
            </div>

            <div className="flex gap-4 items-start" data-id="element-65">
              <div className="mt-1" data-id="element-66">
                <XCircle className="w-5 h-5 text-brand-orange" data-id="element-67" />
              </div>
              <p className="text-brand-slate-light" data-id="element-68">
                <strong data-id="element-69">Las manualidades con pintura</strong> o purpurina crean
                un caos que te obliga a dejar de trabajar para limpiar.
              </p>
            </div>

            <div className="flex gap-4 items-start" data-id="element-70">
              <div className="mt-1" data-id="element-71">
                <XCircle className="w-5 h-5 text-brand-orange" data-id="element-72" />
              </div>
              <p className="text-brand-slate-light" data-id="element-73">
                <strong data-id="element-74">Darle el teléfono</strong> solo crea una adicción que
                hace que las interrupciones sean más violentas la próxima vez.
              </p>
            </div>

            <div className="flex gap-4 items-start" data-id="element-75">
              <div className="mt-1" data-id="element-76">
                <XCircle className="w-5 h-5 text-brand-orange" data-id="element-77" />
              </div>
              <p className="text-brand-slate-light" data-id="element-78">
                <strong data-id="element-79">Decirle "espera un momento"</strong> no funciona porque
                el niño no tiene el concepto del tiempo, solo del aburrimiento.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>;
const UniqueMechanism = () => <section className="py-20 px-4 bg-brand-cream relative overflow-hidden" data-id="element-80">
    <div className="max-w-5xl mx-auto" data-id="element-81">
      <FadeIn data-id="element-82">
        <div className="text-center mb-16" data-id="element-83">
          <span className="text-brand-orange font-bold tracking-wider uppercase text-sm" data-id="element-84">
            El Secreto
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-brand-teal mt-2 mb-6" data-id="element-85">
            El Método de "Alta Retención y Cero Limpieza"
          </h2>
          <p className="text-lg text-brand-slate-light max-w-2xl mx-auto" data-id="element-86">
            A diferencia de los juegos tradicionales, este sistema utiliza
            objetos cotidianos (que ya tienes en tu cocina) configurados bajo el
            Triángulo de Retención.
          </p>
        </div>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-87">
        {[{
        icon: <CheckCircle2 data-id="element-88" />,
        title: 'Visibilidad',
        desc: 'Materiales dispuestos de forma que invitan a la acción inmediata.'
      }, {
        icon: <Zap data-id="element-89" />,
        title: 'Fricción',
        desc: 'El nivel exacto de dificultad para mantenerlos enganchados sin frustrarlos.'
      }, {
        icon: <BrainCircuit data-id="element-90" />,
        title: 'Final Abierto',
        desc: "No hay una forma 'correcta' de jugar, lo que extiende el tiempo de concentración."
      }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-91">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-brand-teal/5 text-center h-full" data-id="element-92">
              <div className="w-12 h-12 bg-brand-teal-light text-brand-teal rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-93">
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-brand-slate mb-2" data-id="element-94">
                {item.title}
              </h3>
              <p className="text-brand-slate-light text-sm" data-id="element-95">{item.desc}</p>
            </div>
          </FadeIn>)}
      </div>

      <FadeIn data-id="element-96">
        <div className="bg-brand-teal text-white p-8 md:p-12 rounded-3xl text-center" data-id="element-97">
          <p className="text-xl md:text-2xl font-serif" data-id="element-98">
            Creamos "misiones" que activan la motricidad fina y la resolución de
            problemas, induciendo un estado de flujo natural que mantiene al
            niño ocupado mientras tú terminas tu trabajo.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const WhatYouGet = () => <section className="py-20 px-4 bg-white" data-id="element-99">
    <div className="max-w-5xl mx-auto" data-id="element-100">
      <FadeIn data-id="element-101">
        <h2 className="text-3xl md:text-4xl font-serif text-brand-teal text-center mb-16" data-id="element-102">
          ¿Qué incluye el Kit?
        </h2>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-103">
        <FadeIn data-id="element-104">
          <img src="https://cdn.magicpatterns.com/uploads/hLsojZjmUpFwHZ7BAV5MKX/30_MINUTOS_DE_SILENCIO_PORTADA.png" alt="Kit Contents" className="rounded-2xl shadow-xl" data-id="element-105" />
        </FadeIn>

        <div className="space-y-6" data-id="element-106">
          {[{
          icon: <FileText data-id="element-107" />,
          text: 'La Guía Maestra PDF con el sistema paso a paso.'
        }, {
          icon: <Timer data-id="element-108" />,
          text: 'El Método 3-2-1 para montar estaciones de juego en menos de 120 segundos.'
        }, {
          icon: <Baby data-id="element-109" />,
          text: "12+ Actividades categorizadas por edad (2 a 6 años) de 'Cero Limpieza'."
        }, {
          icon: <AlertTriangle data-id="element-110" />,
          text: 'El Plan de Crisis de 60 Segundos para detener berrinches en medio de una llamada.'
        }, {
          icon: <CheckCircle2 data-id="element-111" />,
          text: 'Checklist de Materiales Esenciales (que ya están en tu despensa).'
        }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-112">
              <div className="flex gap-4 items-center bg-brand-cream p-4 rounded-xl border border-brand-teal/10" data-id="element-113">
                <div className="text-brand-orange bg-white p-2 rounded-lg shadow-sm" data-id="element-114">
                  {item.icon}
                </div>
                <p className="font-medium text-brand-slate" data-id="element-115">{item.text}</p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </div>
  </section>;
const WhoIsItFor = () => <section className="py-20 px-4 bg-brand-teal-light" data-id="element-116">
    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-117">
      <FadeIn data-id="element-118">
        <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-brand-teal/10 h-full" data-id="element-119">
          <h3 className="text-2xl font-serif text-brand-teal mb-6 flex items-center gap-3" data-id="element-120">
            <CheckCircle2 className="text-green-500" data-id="element-121" /> Para quién ES esto
          </h3>
          <ul className="space-y-4" data-id="element-122">
            {['Padres que hacen Home Office y se sienten agotados por las interrupciones.', 'Freelancers que necesitan bloques de tiempo real para producir.', 'Emprendedores que quieren reducir el tiempo de pantalla de sus hijos de forma orgánica.', 'Familias que buscan fomentar la independencia y la creatividad en sus niños.'].map((text, i) => <li key={i} className="flex gap-3 text-brand-slate-light" data-id="element-123">
                <span className="text-green-500 mt-1" data-id="element-124">•</span>
                <span data-id="element-125">{text}</span>
              </li>)}
          </ul>
        </div>
      </FadeIn>

      <FadeIn delay={0.2} data-id="element-126">
        <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-red-100 h-full" data-id="element-127">
          <h3 className="text-2xl font-serif text-brand-slate mb-6 flex items-center gap-3" data-id="element-128">
            <XCircle className="text-red-500" data-id="element-129" /> Para quién NO es
          </h3>
          <ul className="space-y-4" data-id="element-130">
            {['Personas que buscan actividades artísticas complejas o académicas.', 'Padres que no están dispuestos a dedicar 2 minutos a preparar una misión.', 'Quienes prefieren delegar el 100% de la crianza a dispositivos electrónicos.'].map((text, i) => <li key={i} className="flex gap-3 text-brand-slate-light" data-id="element-131">
                <span className="text-red-500 mt-1" data-id="element-132">•</span>
                <span data-id="element-133">{text}</span>
              </li>)}
          </ul>
        </div>
      </FadeIn>
    </div>
  </section>;
const HowItWorks = () => <section className="py-20 px-4 bg-white" data-id="element-134">
    <div className="max-w-4xl mx-auto" data-id="element-135">
      <FadeIn data-id="element-136">
        <h2 className="text-3xl md:text-4xl font-serif text-brand-teal text-center mb-16" data-id="element-137">
          ¿Cómo funciona?
        </h2>
      </FadeIn>

      <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-138">
        {/* Connecting line for desktop */}
        <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-brand-teal-light z-0" data-id="element-139"></div>

        {[{
        step: '1',
        title: 'Identifica',
        desc: "Tu reunión 'crítica' del día en tu calendario."
      }, {
        step: '2',
        title: 'Elige',
        desc: 'Una misión de la guía según la edad de tu hijo y los materiales a mano.'
      }, {
        step: '3',
        title: 'Monta',
        desc: 'La estación 3-2-1 justo antes de empezar y observa cómo comienza el silencio.'
      }].map((item, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-140">
            <div className="relative z-10 flex flex-col items-center text-center" data-id="element-141">
              <div className="w-16 h-16 bg-brand-teal text-white rounded-full flex items-center justify-center text-2xl font-serif font-bold mb-6 shadow-lg shadow-brand-teal/20 border-4 border-white" data-id="element-142">
                {item.step}
              </div>
              <h3 className="text-xl font-bold text-brand-slate mb-3" data-id="element-143">
                {item.title}
              </h3>
              <p className="text-brand-slate-light" data-id="element-144">{item.desc}</p>
            </div>
          </FadeIn>)}
      </div>
    </div>
  </section>;
const ProofSection = () => <section className="py-20 px-4 bg-brand-cream" data-id="element-145">
    <div className="max-w-3xl mx-auto text-center" data-id="element-146">
      <FadeIn data-id="element-147">
        <h2 className="text-3xl md:text-4xl font-serif text-brand-teal mb-8" data-id="element-148">
          Lo que cambia en 7 días
        </h2>
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-brand-teal/10 relative" data-id="element-149">
          <Coffee className="absolute top-6 left-6 text-brand-orange/20 w-16 h-16" data-id="element-150" />
          <p className="text-lg md:text-xl text-brand-slate-light leading-relaxed relative z-10" data-id="element-151">
            Pasarás de tener el corazón acelerado antes de cada llamada a tener
            la confianza de que tu hijo tiene una misión importante que
            completar. Notarás que la "resaca de pantallas" desaparece,
            reemplazada por un niño que sabe cómo manejar su propio
            aburrimiento.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const FAQ = () => {
  const faqs = [{
    q: '¿Necesito comprar materiales caros?',
    a: 'No, el 95% de las actividades usan cosas como cinta, coladores, cucharas y juguetes que ya tienes en casa.'
  }, {
    q: '¿Cuánto tiempo de silencio puedo esperar?',
    a: 'Entre 15 y 40 minutos dependiendo de la edad y la actividad elegida. Con la práctica, el tiempo de concentración de tu hijo irá aumentando.'
  }, {
    q: '¿Es seguro?',
    a: 'Sí, todas las actividades están diseñadas para ser seguras bajo tu supervisión visual desde el escritorio mientras trabajas.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-20 px-4 bg-white" data-id="element-152">
      <div className="max-w-3xl mx-auto" data-id="element-153">
        <FadeIn data-id="element-154">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-teal text-center mb-12" data-id="element-155">
            Preguntas Frecuentes
          </h2>
        </FadeIn>

        <div className="space-y-4" data-id="element-156">
          {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-157">
              <div className={`border rounded-2xl overflow-hidden transition-colors ${openIndex === i ? 'border-brand-teal bg-brand-teal-light/30' : 'border-gray-200 bg-white'}`} data-id="element-158">
                <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-159">
                  <span className="font-bold text-brand-slate pr-4" data-id="element-160">
                    {faq.q}
                  </span>
                  {openIndex === i ? <ChevronUp className="w-5 h-5 text-brand-teal flex-shrink-0" data-id="element-161" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-162" />}
                </button>
                {openIndex === i && <div className="px-6 pb-5 text-brand-slate-light" data-id="element-163">
                    {faq.a}
                  </div>}
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const OfferCTA = () => <section id="offer" className="py-24 px-4 bg-brand-teal text-white relative overflow-hidden" data-id="element-164">
    {/* Decorative background */}
    <div className="absolute inset-0 opacity-10" data-id="element-165">
      <div className="absolute top-0 left-0 w-full h-full" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }} data-id="element-166"></div>
    </div>

    <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-167">
      <FadeIn data-id="element-168">
        <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-169">
          Recupera tu productividad y la paz en tu hogar hoy mismo.
        </h2>
        <p className="text-xl text-brand-cream/90 mb-12 max-w-2xl mx-auto" data-id="element-170">
          El Kit Silencio Sin Pantallas está diseñado para pagarse solo en tu
          primera hora de trabajo ininterrumpido.
        </p>

        <div id="cta-final" className="bg-white text-brand-slate p-8 md:p-12 rounded-3xl shadow-2xl max-w-2xl mx-auto transform transition-transform hover:scale-[1.02] duration-300 scroll-mt-24" data-id="element-171">
          <div className="uppercase tracking-widest text-sm font-bold text-brand-orange mb-2" data-id="element-172">
            Precio Especial de Lanzamiento
          </div>
          <div className="text-6xl font-serif text-brand-teal mb-8 flex justify-center items-start" data-id="element-173">
            <span className="text-3xl mt-2 mr-1" data-id="element-174">$</span>6.36{' '}
            <span className="text-xl mt-8 ml-2 text-brand-slate-light font-sans" data-id="element-175">
              USD
            </span>
          </div>

          <a href="https://peacefulparentingkit.gumroad.com/l/yxqxg?wanted=true" target="_blank" rel="noopener noreferrer" className="w-full bg-brand-orange hover:bg-brand-orange-dark text-white text-lg md:text-xl font-bold py-5 px-8 rounded-full shadow-lg shadow-brand-orange/30 transition-all hover:-translate-y-1 mb-6 flex flex-col items-center justify-center gap-1" data-id="element-176">
            <span data-id="element-177">¡QUIERO EL KIT Y MIS 30 MINUTOS DE SILENCIO!</span>
          </a>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-brand-slate-light" data-id="element-178">
            <span className="flex items-center gap-1.5" data-id="element-179">
              <Download className="w-4 h-4 text-brand-teal" data-id="element-180" /> Acceso
              instantáneo en PDF
            </span>
            <span className="hidden sm:inline text-gray-300" data-id="element-181">•</span>
            <span className="flex items-center gap-1.5" data-id="element-182">
              <ShieldCheck className="w-4 h-4 text-brand-teal" data-id="element-183" /> Pago 100%
              seguro
            </span>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>;
const LegalModal = ({
  open,
  onClose,
  title,
  children
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!open) return null;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-brand-slate/70 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-184">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.98
    }} transition={{
      duration: 0.2
    }} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-185">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10" data-id="element-186">
          <h3 id="legal-modal-title" className="text-2xl font-serif text-brand-teal" data-id="element-187">
            {title}
          </h3>
          <button onClick={onClose} aria-label="Cerrar" className="w-9 h-9 rounded-full flex items-center justify-center text-brand-slate-light hover:bg-brand-cream transition-colors" data-id="element-188">
            <XCircle className="w-5 h-5" data-id="element-189" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-6 text-brand-slate-light text-sm leading-relaxed space-y-4" data-id="element-190">
          {children}
        </div>
      </motion.div>
    </motion.div>;
};
const PrivacyContent = () => <>
    <p className="text-xs uppercase tracking-wider text-brand-orange font-bold" data-id="element-191">
      Última actualización:{' '}
      {new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    })}
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-192">
      1. Introducción
    </h4>
    <p data-id="element-193">
      En Kit Silencio Sin Pantallas valoramos tu privacidad. Esta política
      describe qué información recopilamos cuando visitas nuestro sitio o
      adquieres nuestro producto digital, y cómo la utilizamos para mejorar tu
      experiencia.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-194">
      2. Información que recopilamos
    </h4>
    <p data-id="element-195">
      Recopilamos únicamente la información necesaria para procesar tu compra y
      entregarte el producto: nombre, correo electrónico y datos de pago
      procesados por nuestra pasarela segura. No almacenamos información de
      tarjetas de crédito en nuestros servidores.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-196">
      3. Uso de la información
    </h4>
    <p data-id="element-197">Usamos tu información para:</p>
    <ul className="list-disc pl-6 space-y-1" data-id="element-198">
      <li data-id="element-199">
        Entregarte el acceso al Kit Silencio Sin Pantallas en formato PDF.
      </li>
      <li data-id="element-200">
        Enviarte actualizaciones o material complementario relacionado con tu
        compra.
      </li>
      <li data-id="element-201">Cumplir con obligaciones legales y fiscales aplicables.</li>
    </ul>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-202">
      4. Cookies y tecnologías similares
    </h4>
    <p data-id="element-203">
      Utilizamos cookies para analizar el tráfico del sitio y mejorar la
      experiencia del usuario. Puedes desactivarlas desde la configuración de tu
      navegador en cualquier momento.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-204">
      5. Compartir información con terceros
    </h4>
    <p data-id="element-205">
      No vendemos ni alquilamos tu información personal. Solo compartimos datos
      con proveedores estrictamente necesarios para procesar pagos y entregar el
      producto digital (por ejemplo, pasarelas de pago y servicios de email).
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-206">
      6. Tus derechos
    </h4>
    <p data-id="element-207">
      Tienes derecho a acceder, rectificar o eliminar tus datos personales en
      cualquier momento. Para ejercer estos derechos, podrás solicitarlo a
      través de los canales habilitados en tu confirmación de compra.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-208">
      7. Cambios a esta política
    </h4>
    <p data-id="element-209">
      Nos reservamos el derecho de actualizar esta política. Cualquier cambio
      sustancial será notificado a través de este sitio.
    </p>
  </>;
const TermsContent = () => <>
    <p className="text-xs uppercase tracking-wider text-brand-orange font-bold" data-id="element-210">
      Última actualización:{' '}
      {new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    })}
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-211">
      1. Aceptación de los términos
    </h4>
    <p data-id="element-212">
      Al adquirir y/o utilizar el Kit Silencio Sin Pantallas, aceptas estos
      términos y condiciones en su totalidad. Si no estás de acuerdo con alguno
      de ellos, te pedimos no realizar la compra.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-213">
      2. Producto digital
    </h4>
    <p data-id="element-214">
      El Kit Silencio Sin Pantallas es un producto digital en formato PDF
      descargable. Tras completar el pago, recibirás acceso instantáneo al
      material a través del correo electrónico proporcionado durante la compra.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-215">
      3. Licencia de uso
    </h4>
    <p data-id="element-216">
      La compra otorga una licencia personal, intransferible y no exclusiva para
      uso individual. Está prohibido revender, redistribuir, copiar o compartir
      el contenido total o parcialmente sin autorización expresa.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-217">
      4. Pagos y reembolsos
    </h4>
    <p data-id="element-218">
      Todos los pagos se procesan en dólares estadounidenses (USD) a través de
      pasarelas seguras. Dado que se trata de un producto digital de entrega
      inmediata, las compras son finales y no admiten reembolsos una vez
      entregado el material, salvo en casos excepcionales contemplados por la
      ley aplicable.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-219">
      5. Naturaleza del contenido
    </h4>
    <p data-id="element-220">
      El contenido del kit tiene fines informativos y educativos. Las
      actividades sugeridas requieren la supervisión visual de un adulto
      responsable. Kit Silencio Sin Pantallas no se hace responsable del uso
      indebido de los materiales o actividades descritas.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-221">
      6. Propiedad intelectual
    </h4>
    <p data-id="element-222">
      Todos los textos, imágenes, ilustraciones y materiales del kit están
      protegidos por derechos de autor y son propiedad exclusiva de Kit Silencio
      Sin Pantallas. Su reproducción no autorizada constituye una infracción
      legal.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-223">
      7. Limitación de responsabilidad
    </h4>
    <p data-id="element-224">
      Los resultados pueden variar de un niño a otro. No garantizamos resultados
      específicos en cuanto a tiempos de concentración o comportamiento, ya que
      dependen de múltiples factores fuera de nuestro control.
    </p>

    <h4 className="text-lg font-bold text-brand-slate font-serif" data-id="element-225">
      8. Modificaciones
    </h4>
    <p data-id="element-226">
      Nos reservamos el derecho de actualizar estos términos en cualquier
      momento. La versión vigente será siempre la publicada en este sitio.
    </p>
  </>;
const Footer = () => {
  const [modal, setModal] = useState<null | 'privacy' | 'terms'>(null);
  return <>
      <footer className="bg-brand-slate text-brand-slate-light py-12 text-center text-sm" data-id="element-227">
        <div className="max-w-6xl mx-auto px-4" data-id="element-228">
          <div className="flex items-center justify-center gap-2 font-serif text-xl text-white mb-6" data-id="element-229">
            <Clock className="w-5 h-5 text-brand-orange" data-id="element-230" />
            <span data-id="element-231">Kit Silencio</span>
          </div>
          <p data-id="element-232">
            © {new Date().getFullYear()} Kit Silencio Sin Pantallas. Todos los
            derechos reservados.
          </p>
          <p className="mt-2 mb-6" data-id="element-233">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm" data-id="element-234">
            <button onClick={() => setModal('privacy')} className="hover:text-brand-orange transition-colors underline-offset-4 hover:underline" data-id="element-235">
              Política de Privacidad
            </button>
            <span className="text-brand-slate-light/40" data-id="element-236">•</span>
            <button onClick={() => setModal('terms')} className="hover:text-brand-orange transition-colors underline-offset-4 hover:underline" data-id="element-237">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal open={modal === 'privacy'} onClose={() => setModal(null)} title="Política de Privacidad" data-id="element-238">
        <PrivacyContent data-id="element-239" />
      </LegalModal>

      <LegalModal open={modal === 'terms'} onClose={() => setModal(null)} title="Términos y Condiciones" data-id="element-240">
        <TermsContent data-id="element-241" />
      </LegalModal>
    </>;
};
export function App() {
  return <div className="min-h-screen bg-brand-cream font-sans selection:bg-brand-orange/30 selection:text-brand-teal" data-id="element-242">
      <Navbar data-id="element-243" />
      <main data-id="element-244">
        <Hero data-id="element-245" />
        <OpeningHook data-id="element-246" />
        <ProblemSection data-id="element-247" />
        <UniqueMechanism data-id="element-248" />
        <WhatYouGet data-id="element-249" />
        <WhoIsItFor data-id="element-250" />
        <HowItWorks data-id="element-251" />
        <ProofSection data-id="element-252" />
        <FAQ data-id="element-253" />
        <OfferCTA data-id="element-254" />
      </main>
      <Footer data-id="element-255" />
    </div>;
}